import { useEffect, useState } from 'react';
import { useChatSyncConfig } from '../../api/hooks';
import { useSaveChatSyncConfig } from '../../api/mutations';
import { btnPrimary, fieldLabelCls, inputCls } from '../../lib/styles';
import { Modal } from '../Modal';

/**
 * Настройка источника синхронизации чатов (URL + Bearer-токен внешнего API +
 * вкл/выкл авто-синхрона). Токен наружу не отдаётся — показываем только факт
 * «задан»; пустое поле токена при сохранении = не менять. Доступно admin
 * (telegram.manage). Ручной запуск — отдельная кнопка на вкладке «Чаты».
 */
export function ChatSyncConfigModal({ onClose }: { onClose: () => void }) {
  const { data: config, isLoading } = useChatSyncConfig();
  const save = useSaveChatSyncConfig();
  const [enabled, setEnabled] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setApiUrl(config.apiUrl ?? '');
    }
  }, [config]);

  async function doSave() {
    setError(null);
    setSaved(false);
    try {
      await save.mutateAsync({
        enabled,
        apiUrl: apiUrl.trim() || null,
        // Токен шлём только если оператор ввёл новый — иначе оставляем как есть.
        ...(apiToken.trim() ? { apiToken: apiToken.trim() } : {}),
      });
      setApiToken('');
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Modal title="Источник синхронизации чатов" onClose={onClose} size="md">
      {isLoading ? (
        <div className="text-sm text-text2">Загрузка…</div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-text2">
            Внешний read-only API, из которого раз в сутки подтягивается список Telegram-групп.
            Новые чаты добавляются, у существующих обновляется только название; ничего не удаляется.
          </p>

          <label className="flex items-center gap-2 text-sm text-text">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Авто-синхронизация (раз в сутки)
          </label>

          <div>
            <label className={fieldLabelCls}>URL источника (эндпоинт /groups)</label>
            <input
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              placeholder="http://host:port/groups"
              className={`${inputCls} w-full font-mono`}
            />
          </div>

          <div>
            <label className={fieldLabelCls}>Токен (Bearer)</label>
            <input
              type="password"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              placeholder={config?.tokenSet ? '•••••• задан (пусто = не менять)' : 'токен не задан'}
              className={`${inputCls} w-full font-mono`}
            />
          </div>

          {config && (config.lastSyncAt || config.lastSyncStatus) && (
            <div className="rounded-md border border-border-subtle bg-bg p-3 text-xs text-text2">
              Последняя синхронизация:{' '}
              {config.lastSyncAt ? new Date(config.lastSyncAt).toLocaleString('ru-RU') : '—'} —{' '}
              <span className={config.lastSyncStatus === 'ok' ? 'text-success' : 'text-danger'}>
                {config.lastSyncStatus === 'ok'
                  ? 'успех'
                  : config.lastSyncStatus === 'failed'
                    ? 'ошибка'
                    : '—'}
              </span>
              {config.lastSyncError && <div className="mt-1 text-danger">{config.lastSyncError}</div>}
            </div>
          )}

          {error && <div className="text-xs text-danger">{error}</div>}
          {saved && <div className="text-xs text-success">✓ сохранено</div>}

          <div className="flex justify-end">
            <button onClick={() => void doSave()} disabled={save.isPending} className={btnPrimary}>
              {save.isPending ? '…' : 'Сохранить'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
